/** Form primitives — Input, Textarea, Label, FormField (with error/required/help). */
import { forwardRef } from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cn } from "../../lib/cn";

const inputBase =
  "w-full bg-surface border border-line-2 text-text placeholder:text-text-faint rounded-[14px] px-4 py-3 text-[14.5px] transition-colors duration-200 ease-nvl focus:outline-none focus:border-mauve focus:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed";

export const Input = forwardRef(function Input({ className, ...props }, ref) {
  return <input ref={ref} className={cn(inputBase, className)} {...props} />;
});

export const Textarea = forwardRef(function Textarea({ className, rows = 4, ...props }, ref) {
  return <textarea ref={ref} rows={rows} className={cn(inputBase, "resize-y leading-relaxed", className)} {...props} />;
});

export const Label = forwardRef(function Label({ className, children, required, ...props }, ref) {
  return (
    <LabelPrimitive.Root
      ref={ref}
      className={cn("text-[12.5px] font-mono uppercase tracking-[0.16em] text-text-dim", className)}
      {...props}
    >
      {children}
      {required && <span className="text-mauve ml-1">*</span>}
    </LabelPrimitive.Root>
  );
});

/** FormField — Label + control + help/error. */
export function FormField({ id, label, required, help, error, children, className }) {
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {label && (
        <Label htmlFor={id} required={required}>
          {label}
        </Label>
      )}
      {children}
      {error ? (
        <p className="text-[12.5px] text-red-400 flex items-start gap-1.5" data-testid={`${id}-error`}>
          <span className="mt-0.5">⚠</span> {error}
        </p>
      ) : help ? (
        <p className="text-[12.5px] text-text-faint">{help}</p>
      ) : null}
    </div>
  );
}
